// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminAdManager\AdminAdManager.jsx
// Название: Admin Ad Manager Dashboard
// Версия: 1.0
// Дата: 18.02.2026

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './AdminAdManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com/api';

const AdminAdManager = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('authToken');

  // ============================================
  // СЕКЦИЯ: State
  // ============================================
  const [platforms, setPlatforms] = useState([]);
  const [adCampaigns, setAdCampaigns] = useState([]);
  const [daryCampaigns, setDaryCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Launch form
  const [showLaunchForm, setShowLaunchForm] = useState(false);
  const [launchForm, setLaunchForm] = useState({
    campaignId: '',
    platform: 'google',
    dailyBudget: 10,
    maxSpendPerAd: 3,
    maxHours: 6,
    country: 'FR'
  });

  // OAuth messages
  const oauthConnected = searchParams.get('connected');
  const oauthError = searchParams.get('error');

  // ============================================
  // СЕКЦИЯ: API helpers
  // ============================================
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const apiFetch = useCallback(async (endpoint, options = {}) => {
    const res = await fetch(`${API_URL}${endpoint}`, { headers, ...options });
    return res.json();
  }, [token]);

  // ============================================
  // СЕКЦИЯ: Load data
  // ============================================
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [platformsRes, adCampsRes, daryCampsRes] = await Promise.all([
        apiFetch('/ad-manager/platforms'),
        apiFetch('/ad-manager/campaigns'),
        apiFetch('/admin/campaigns?limit=50&status=active')
      ]);

      if (platformsRes.success) setPlatforms(platformsRes.data || []);
      if (adCampsRes.success) setAdCampaigns(adCampsRes.data || []);
      if (daryCampsRes.success) setDaryCampaigns(daryCampsRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // СЕКЦИЯ: Действия
  // ============================================
  const handleConnectGoogle = async () => {
    try {
      const res = await apiFetch('/ad-manager/oauth/google/start', { method: 'POST' });
      if (res.success && res.data?.authUrl) {
        window.location.href = res.data.authUrl;
      } else {
        setError(res.error || 'Failed to start OAuth');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await apiFetch('/ad-manager/sync', { method: 'POST' });
      if (res.success) {
        setSuccessMsg(`Synced! Paused: ${res.data.overspend || 0} overspend, ${res.data.timeout || 0} timeout`);
        await loadData();
      } else {
        setError(res.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleLaunch = async () => {
    if (!launchForm.campaignId) {
      setError('Select a campaign');
      return;
    }
    setLaunching(true);
    setError(null);
    try {
      const res = await apiFetch('/ad-manager/launch', {
        method: 'POST',
        body: JSON.stringify({
          campaignId: parseInt(launchForm.campaignId),
          platform: launchForm.platform,
          dailyBudget: parseFloat(launchForm.dailyBudget),
          maxSpendPerAd: parseFloat(launchForm.maxSpendPerAd),
          maxHours: parseInt(launchForm.maxHours),
          targeting: { country: launchForm.country }
        })
      });
      if (res.success) {
        setSuccessMsg(`Launched! ${res.data.adsCreated}/${res.data.adsTotal} ads created`);
        setShowLaunchForm(false);
        await loadData();
      } else {
        setError(res.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLaunching(false);
    }
  };

  const handleToggleCampaign = async (adCampaignId, action) => {
    try {
      const res = await apiFetch(`/ad-manager/campaigns/${adCampaignId}/${action}`, { method: 'POST' });
      if (res.success) {
        setSuccessMsg(`Campaign ${action}d`);
        await loadData();
      } else {
        setError(res.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================================
  // СЕКЦИЯ: Helpers
  // ============================================
  const getStatusBadge = (status) => {
    const map = {
      active: { class: 'status-active', label: '🟢 Active' },
      paused: { class: 'status-paused', label: '⏸ Paused' },
      completed: { class: 'status-completed', label: '✅ Completed' },
      creating: { class: 'status-creating', label: '⏳ Creating' },
      draft: { class: 'status-draft', label: '📝 Draft' },
      error: { class: 'status-error', label: '❌ Error' },
      disconnected: { class: 'status-error', label: '🔴 Disconnected' },
      connecting: { class: 'status-creating', label: '🔄 Connecting' }
    };
    return map[status] || { class: '', label: status };
  };

  const formatEur = (amount) => `€${(parseFloat(amount) || 0).toFixed(2)}`;

  // ============================================
  // СЕКЦИЯ: Render
  // ============================================
  if (loading) {
    return <div className="adm-container"><div className="adm-loading">Loading Ad Manager...</div></div>;
  }

  const googlePlatform = platforms.find(p => p.platform === 'google');
  const isGoogleConnected = googlePlatform?.status === 'active';

  return (
    <div className="adm-container">
      <div className="adm-header">
        <div>
          <h1>📢 Ad Manager</h1>
          <p className="adm-subtitle">Google Ads integration & campaign management</p>
        </div>
        <div className="adm-header-actions">
          <button className="adm-btn adm-btn-secondary" onClick={handleSync} disabled={syncing}>
            {syncing ? '⏳ Syncing...' : '🔄 Sync Stats'}
          </button>
          <button className="adm-btn adm-btn-primary" onClick={() => setShowLaunchForm(true)} disabled={!isGoogleConnected}>
            🚀 Launch Ads
          </button>
        </div>
      </div>

      {/* Messages */}
      {oauthConnected && <div className="adm-msg adm-msg-success">✅ Google Ads connected successfully!</div>}
      {oauthError && <div className="adm-msg adm-msg-error">❌ OAuth error: {oauthError}</div>}
      {error && <div className="adm-msg adm-msg-error">❌ {error} <button onClick={() => setError(null)}>✕</button></div>}
      {successMsg && <div className="adm-msg adm-msg-success">✅ {successMsg} <button onClick={() => setSuccessMsg(null)}>✕</button></div>}

      {/* ============================================ */}
      {/* ПЛАТФОРМЫ */}
      {/* ============================================ */}
      <section className="adm-section">
        <h2>🔗 Ad Platforms</h2>
        <div className="adm-platforms">
          {/* Google Ads */}
          <div className={`adm-platform-card ${isGoogleConnected ? 'connected' : ''}`}>
            <div className="adm-platform-header">
              <span className="adm-platform-icon">🔵</span>
              <div>
                <strong>Google Ads</strong>
                <span className={`adm-status ${getStatusBadge(googlePlatform?.status || 'disconnected').class}`}>
                  {getStatusBadge(googlePlatform?.status || 'disconnected').label}
                </span>
              </div>
            </div>
            {isGoogleConnected ? (
              <div className="adm-platform-info">
                <p>Account: {googlePlatform.account_name || googlePlatform.account_id || 'Connected'}</p>
                {googlePlatform.last_sync_at && (
                  <p className="adm-platform-sync">Last sync: {new Date(googlePlatform.last_sync_at).toLocaleString()}</p>
                )}
              </div>
            ) : (
              <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={handleConnectGoogle}>
                Connect Google Ads
              </button>
            )}
            {googlePlatform?.last_error && (
              <p className="adm-platform-error">{googlePlatform.last_error}</p>
            )}
          </div>

          {/* Meta Ads - Coming Soon */}
          <div className="adm-platform-card disabled">
            <div className="adm-platform-header">
              <span className="adm-platform-icon">🟣</span>
              <div>
                <strong>Meta Ads</strong>
                <span className="adm-status status-draft">Coming Soon</span>
              </div>
            </div>
          </div>

          {/* TikTok - Coming Soon */}
          <div className="adm-platform-card disabled">
            <div className="adm-platform-header">
              <span className="adm-platform-icon">⚫</span>
              <div>
                <strong>TikTok Ads</strong>
                <span className="adm-status status-draft">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* LAUNCH FORM */}
      {/* ============================================ */}
      {showLaunchForm && (
        <section className="adm-section adm-launch-section">
          <h2>🚀 Launch Ads for Campaign</h2>
          <div className="adm-launch-form">
            <div className="adm-form-row">
              <label>DARY Campaign</label>
              <select value={launchForm.campaignId} onChange={e => setLaunchForm({...launchForm, campaignId: e.target.value})}>
                <option value="">-- Select campaign --</option>
                {daryCampaigns.map(c => (
                  <option key={c.id} value={c.id}>#{c.id} {c.name} ({c.gifts_count || '?'} gifts)</option>
                ))}
              </select>
            </div>
            <div className="adm-form-grid">
              <div className="adm-form-row">
                <label>Daily Budget (€)</label>
                <input type="number" value={launchForm.dailyBudget} onChange={e => setLaunchForm({...launchForm, dailyBudget: e.target.value})} />
              </div>
              <div className="adm-form-row">
                <label>Max Spend per Ad (€)</label>
                <input type="number" value={launchForm.maxSpendPerAd} step="0.5" onChange={e => setLaunchForm({...launchForm, maxSpendPerAd: e.target.value})} />
              </div>
              <div className="adm-form-row">
                <label>Max Hours per Ad</label>
                <input type="number" value={launchForm.maxHours} onChange={e => setLaunchForm({...launchForm, maxHours: e.target.value})} />
              </div>
              <div className="adm-form-row">
                <label>Country</label>
                <select value={launchForm.country} onChange={e => setLaunchForm({...launchForm, country: e.target.value})}>
                  <option value="FR">🇫🇷 France</option>
                  <option value="DE">🇩🇪 Germany</option>
                  <option value="US">🇺🇸 USA</option>
                  <option value="GB">🇬🇧 UK</option>
                  <option value="UA">🇺🇦 Ukraine</option>
                  <option value="IT">🇮🇹 Italy</option>
                  <option value="ES">🇪🇸 Spain</option>
                  <option value="NL">🇳🇱 Netherlands</option>
                  <option value="PL">🇵🇱 Poland</option>
                  <option value="BE">🇧🇪 Belgium</option>
                </select>
              </div>
            </div>
            <div className="adm-form-actions">
              <button className="adm-btn adm-btn-secondary" onClick={() => setShowLaunchForm(false)}>Cancel</button>
              <button className="adm-btn adm-btn-primary" onClick={handleLaunch} disabled={launching}>
                {launching ? '⏳ Creating ads...' : '🚀 Launch'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ============================================ */}
      {/* AD CAMPAIGNS */}
      {/* ============================================ */}
      <section className="adm-section">
        <h2>📋 Ad Campaigns ({adCampaigns.length})</h2>
        {adCampaigns.length === 0 ? (
          <div className="adm-empty">
            <p>No ad campaigns yet. Launch your first one!</p>
          </div>
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Campaign</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Ads</th>
                  <th>Impressions</th>
                  <th>Clicks</th>
                  <th>Spent</th>
                  <th>Redeemed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {adCampaigns.map(ac => {
                  const st = getStatusBadge(ac.status);
                  return (
                    <tr key={ac.id}>
                      <td>#{ac.id}</td>
                      <td>
                        <strong>{ac.campaign_name || `Campaign #${ac.campaign_id}`}</strong>
                        {ac.brand_name && <br />}
                        {ac.brand_name && <small>{ac.brand_name}</small>}
                      </td>
                      <td>{ac.platform === 'google' ? '🔵 Google' : ac.platform}</td>
                      <td><span className={`adm-status ${st.class}`}>{st.label}</span></td>
                      <td>
                        <span className="adm-stat-green">{ac.redeemed_units || 0}</span>
                        /
                        <span className="adm-stat-blue">{ac.active_units || 0}</span>
                        /
                        <span>{ac.total_units || 0}</span>
                      </td>
                      <td>{(ac.total_impressions || 0).toLocaleString()}</td>
                      <td>{(ac.total_clicks || 0).toLocaleString()}</td>
                      <td>{formatEur(ac.total_spent_eur)}</td>
                      <td>
                        <strong className="adm-stat-green">{ac.redeemed_units || 0}</strong>
                        <small> / {ac.total_units || 0}</small>
                      </td>
                      <td className="adm-actions">
                        <button className="adm-btn adm-btn-sm" onClick={() => navigate(`/admin/ad-manager/${ac.id}`)}>
                          👁 Details
                        </button>
                        {ac.status === 'active' && (
                          <button className="adm-btn adm-btn-sm adm-btn-warn" onClick={() => handleToggleCampaign(ac.id, 'pause')}>
                            ⏸ Pause
                          </button>
                        )}
                        {ac.status === 'paused' && (
                          <button className="adm-btn adm-btn-sm adm-btn-success" onClick={() => handleToggleCampaign(ac.id, 'resume')}>
                            ▶ Resume
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminAdManager;